import type { ApolloError } from '@apollo/client';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { bigNumberWithCommas } from '@openx/utils/lib/bigNumberWithCommas.ts';
import { useTranslation } from 'react-i18next';

const formatTotalReach = (reachValue: number, rangeFactor = 0.05) =>
  reachValue
    ? [reachValue * (1 - rangeFactor), reachValue * (1 + rangeFactor)]
        .map(Math.round)
        .map(bigNumberWithCommas)
        .join(' - ')
    : '0';

export type ReachProps = {
  isFetchingEstimates: boolean;
  fetchingError?: ApolloError;
  OAID: number;
  OAIDError?: boolean;
  legacyReachTotal: number;
  isIncludesEmpty: boolean;
  dataTest?: string;
};

export const Reach = ({
  isFetchingEstimates,
  fetchingError,
  OAIDError,
  OAID,
  legacyReachTotal,
  isIncludesEmpty,
}: ReachProps) => {
  const { t } = useTranslation();

  if (isIncludesEmpty) {
    return <Typography variant="h2">{0}</Typography>;
  }

  if (isFetchingEstimates) {
    return <CircularProgress size={'16px'} />;
  }

  if (fetchingError || OAIDError) {
    return (
      <Box
        data-test="estimated-reach-without-data"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography variant="h2" sx={{ color: 'text.disabled' }}>
          {t('N / A')}
        </Typography>
        <Tooltip title={t('Unable to generate reach estimates')}>
          <IconButton size="large">
            <InfoRoundedIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Typography data-test="estimated-reach-value" variant="h2">
      {formatTotalReach(OAID > 0 ? OAID : legacyReachTotal)}
    </Typography>
  );
};
